// src/pages/admin/emails/AdminEmailRules.tsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabaseClient";
import RuleForm from "./components/RuleForm";

type RuleRow = {
  id: string;
  template_id: string;
  is_active: boolean;
  trigger_type: string;
  condition_json: any;
  audience_sql: string | null;
  not_before_seconds: number;
  priority: number;
  created_at: string;
  updated_at: string;
  email_templates: {
    id: string;
    name: string;
    enabled: boolean;
    allow_tracking: boolean;
    category: string;
  } | null;
};

export default function AdminEmailRules() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<RuleRow[]>([]);
  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState<boolean | "all">("all");
  const [formOpen, setFormOpen] = useState(false);
  const [editRow, setEditRow] = useState<RuleRow | null>(null);

  const filtered = useMemo(() => {
    const f = rows.filter((r) => {
      const tplName = r.email_templates?.name ?? "";
      const cat = r.email_templates?.category ?? "";
      const matchesQ =
        !q ||
        tplName.toLowerCase().includes(q.toLowerCase()) ||
        cat.toLowerCase().includes(q.toLowerCase()) ||
        r.trigger_type.toLowerCase().includes(q.toLowerCase());
      const matchesActive =
        onlyActive === "all" ? true : r.is_active === Boolean(onlyActive);
      return matchesQ && matchesActive;
    });
    return f.sort((a, b) => {
      // erst aktive, dann nach Priorität, dann aktualisiert
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      if (a.priority !== b.priority) return a.priority - b.priority;
      return b.updated_at.localeCompare(a.updated_at);
    });
  }, [rows, q, onlyActive]);

  async function fetchRows() {
    setLoading(true);
    const { data, error } = await supabase
      .from("email_rules")
      .select(`
        id, template_id, is_active, trigger_type, condition_json,
        audience_sql, not_before_seconds, priority, created_at, updated_at,
        email_templates ( id, name, enabled, allow_tracking, category )
      `)
      .order("updated_at", { ascending: false });
    if (error) {
      console.error(error);
      setRows([]);
    } else {
      setRows((data ?? []) as unknown as RuleRow[]);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchRows();
  }, []);

  function onCreate() {
    setEditRow(null);
    setFormOpen(true);
  }
  function onEdit(row: RuleRow) {
    setEditRow(row);
    setFormOpen(true);
  }
  function onSaved() {
    setFormOpen(false);
    setEditRow(null);
    fetchRows();
  }

  async function toggleActive(row: RuleRow) {
    const { error } = await supabase
      .from("email_rules")
      .update({ is_active: !row.is_active })
      .eq("id", row.id);
    if (error) return alert(error.message);
    fetchRows();
  }

  async function deleteRule(row: RuleRow) {
    const really = window.confirm(
    `Regel zu Vorlage "${row.email_templates?.name ?? row.template_id}" wirklich löschen?`
    );
    if (!really) return;
    const { error } = await supabase.from("email_rules").delete().eq("id", row.id);
    if (error) return alert(error.message);
    fetchRows();
  }

  // Hilfslink: Dry-Run aufrufen (nur Vorschau, keine Writes)
  function dryRunUrl(hours = 6) {
    // Passe Basis-URL an dein Projekt an oder nutze relative URL über Reverse Proxy
    return `/functions/v1/email-evaluator-dry-run?hours=${hours}&limit=50&only_beta=true`;
  }

  return (
    <div className="max-w-6xl mx-auto mt-10 p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-sky-400">⚙️ Regeln</h1>
        <div className="flex items-center gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suche (Vorlage, Kategorie, Trigger)…"
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded-lg w-64 outline-none focus:border-sky-400 placeholder:text-slate-600"
          />
          <select
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded-lg outline-none focus:border-sky-400"
            value={String(onlyActive)}
            onChange={(e) =>
              setOnlyActive(
                e.target.value === "all" ? "all" : e.target.value === "true"
              )
            }
          >
            <option value="all">Alle</option>
            <option value="true">Nur aktiv</option>
            <option value="false">Nur inaktiv</option>
          </select>
          <button
            onClick={onCreate}
            className="px-4 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600 transition-colors"
          >
            + Neue Regel
          </button>
        </div>
      </div>

      <p className="text-slate-400 mb-4">
        Regeln steuern, <em>wann</em> eine Vorlage eingeplant wird. Tipp: Nutze den{" "}
        <a href={dryRunUrl()} className="text-sky-400 hover:underline" target="_blank" rel="noreferrer">
          Dry-Run
        </a>{" "}
        um Kandidaten gefahrlos zu prüfen.
      </p>

      {loading ? (
        <div className="p-6 border border-[#2a3347] rounded-xl bg-[#161b27] text-slate-400">Lade…</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 border border-[#2a3347] rounded-xl bg-[#161b27] text-slate-500">
          Keine Regeln gefunden.
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#2a3347] rounded-xl bg-[#161b27]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#1e2639] text-left">
              <tr>
                <th className="px-4 py-3 font-semibold text-slate-300">Vorlage</th>
                <th className="px-4 py-3 font-semibold text-slate-300">Trigger</th>
                <th className="px-4 py-3 font-semibold text-slate-300">Delay</th>
                <th className="px-4 py-3 font-semibold text-slate-300">Priorität</th>
                <th className="px-4 py-3 font-semibold text-slate-300">Aktiv</th>
                <th className="px-4 py-3 font-semibold text-slate-300">Bedingung</th>
                <th className="px-4 py-3 font-semibold text-slate-300 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-[#2a3347] align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-100">{r.email_templates?.name ?? "—"}</div>
                    <div className="text-xs text-slate-500">{r.email_templates?.category ?? "—"}</div>
                    <div className="text-xs text-slate-400">
                      Template:{" "}
                      <span className={r.email_templates?.enabled ? "text-emerald-400" : "text-slate-500"}>
                        {r.email_templates?.enabled ? "aktiv" : "inaktiv"}
                      </span>
                      {r.email_templates?.allow_tracking ? " • Tracking: an" : ""}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{r.trigger_type}</td>
                  <td className="px-4 py-3 text-slate-300">{formatDelay(r.not_before_seconds)}</td>
                  <td className="px-4 py-3 text-slate-300">{r.priority}</td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        "inline-flex px-2 py-0.5 rounded-full text-xs " +
                        (r.is_active
                          ? "bg-emerald-900/30 text-emerald-400"
                          : "bg-[#2a3347] text-slate-400")
                      }
                    >
                      {r.is_active ? "aktiv" : "inaktiv"}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-[360px]">
                    <pre className="text-xs whitespace-pre-wrap bg-[#0d1117] border border-[#2a3347] rounded p-2 text-slate-300">
                      {prettyJson(r.condition_json)}
                    </pre>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 justify-end">
                      <a
                        href={dryRunUrl()}
                        target="_blank"
                        rel="noreferrer"
                        className="px-2 py-1 text-sky-400 hover:underline"
                      >
                        Dry-Run
                      </a>
                      <button
                        onClick={() => onEdit(r)}
                        className="px-2 py-1 text-slate-300 hover:underline"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => toggleActive(r)}
                        className="px-2 py-1 text-slate-300 hover:underline"
                      >
                        {r.is_active ? "Deaktivieren" : "Aktivieren"}
                      </button>
                      <button
                        onClick={() => deleteRule(r)}
                        className="px-2 py-1 text-red-400 hover:underline"
                      >
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-8">
        <Link to="/admin/emails" className="text-sky-400 hover:underline">
          ← Zurück zum E-Mail-Bereich
        </Link>
      </div>

      {formOpen && (
        <RuleForm
          initial={editRow ?? undefined}
          onClose={() => {
            setFormOpen(false);
            setEditRow(null);
          }}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function formatDelay(seconds: number) {
  if (!seconds || seconds <= 0) return "ohne Verzögerung";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function prettyJson(obj: any) {
  try {
    return JSON.stringify(obj ?? {}, null, 2);
  } catch {
    return String(obj ?? "");
  }
}
